import { z } from 'zod';

import { DataChangeActionEnum, DeclarationStatus, ReportStatus, TemplateType } from '@e-origin/shared';

import { BatchStatisticsSchema } from './BatchStatisticsSchema';
import { ValuationInformationTaxesSchema } from './ValuationInformationTaxesSchema';

const objectIdLike = z.any();

const BatchStatusSchema = z.object({
  value: z.string(),
  details: z.string().optional().nullable(),
  subStatus: z.string().optional().nullable(),
  brokerStatus: z.string().optional().nullable(),
});

const BatchRiskAnalysisSchema = z.object({
  impactInEuro: z.number().optional(),
  impactInPercentage: z.number().optional(),
  totalAmountForDutiesAndTaxes: z.number().optional(),
  currentAmountForDutiesAndTaxesSum: z.number().optional(),
  initialAmountToPayForDuties: z.number().optional(),
});

const BatchTraceSchema = z.object({
  userId: objectIdLike.optional(),
  action: z.string().optional(),
  dateTime: z.coerce.date().optional(),
  dataChanges: z
    .array(
      z.object({
        fieldName: z.string().optional(),
        action: z.nativeEnum(DataChangeActionEnum).optional(),
        oldFieldValue: z.string().optional(),
        newFieldValue: z.string().optional(),
      }),
    )
    .optional(),
});

const BatchReportSchema = z.object({
  status: z.nativeEnum(ReportStatus).optional(),
  lrnsTotal: z.number().optional(),
  lrns: z
    .array(
      z.object({
        lrn: z.string().optional(),
        statuses: z.array(z.object({ status: z.nativeEnum(DeclarationStatus).optional(), count: z.number().optional() })).optional(),
      }),
    )
    .optional(),
  requestDate: z.coerce.date().optional(),
  requestedBy: z.array(objectIdLike).optional(),
});

const BatchReportsSchema = z.object({
  customsReport: BatchReportSchema.optional(),
  utbReport: BatchReportSchema.optional(),
});

const BatchVirtualsSchema = z.object({
  hasFileInCloud: z.boolean().optional(),
  missingSequence1Lrns: z.array(z.string()).optional(),
  templateName: z.string().optional(),
  templateVersion: z.number().optional(),
  templatePublished: z.boolean().optional(),
  templateVersioningId: objectIdLike.optional(),
  customerName: z.string().optional(),
  brokerName: z.string().optional(),
  sendActionPending: z.boolean().optional(),
});

export const BatchListItemSchema = z.object({
  _id: objectIdLike,
  name: z.string(),
  counter: z.number(),
  filename: z.string(),
  useCustomsSimulator: z.boolean().optional(),
  customer: objectIdLike.optional(),
  template: objectIdLike.optional(),
  templateType: z.nativeEnum(TemplateType).optional(),
  isExport: z.boolean().optional(),
  transactionType: z.number().optional(),
  status: BatchStatusSchema.optional(),
  declarant: objectIdLike.optional(),
  cloudBucketFilesUrls: z.array(z.any()).optional(),
  cloudBucketFileName: z.string().optional(),
  cloudBucketInvoiceUrl: z.string().optional(),
  cloudBucketBeGateFile: z.string().optional(),
  deleted: z.boolean().optional(),
  riskAnalysis: BatchRiskAnalysisSchema.optional(),
  statistics: BatchStatisticsSchema.optional(),
  summary: z.object({ totalGrossMass: z.number().optional() }).optional(),
  commentsCount: z.number().optional(),
  allDeclarationsSent: z.boolean().optional(),
  allDeclarationsReleased: z.boolean().optional(),
  sendingProgress: z.number().optional(),
  createdBy: objectIdLike.optional(),
  trace: z.array(BatchTraceSchema).optional(),
  canCleanup: z.boolean().optional(),
  canDelete: z.boolean().optional(),
  valuationInformationTaxes: ValuationInformationTaxesSchema.optional(),
  transit: objectIdLike.optional(),
  forwarder: objectIdLike.optional(),
  broker: objectIdLike.optional(),
  externalId: z.string().optional(),
  reports: BatchReportsSchema.optional(),
  archived: z.boolean().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  virtuals: BatchVirtualsSchema.optional(),
});

export type BatchListItem = z.infer<typeof BatchListItemSchema>;
